import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useOrg } from "@/contexts/OrgContext";
import { useTenantPath } from "@/lib/tenantPath";
import { useRole } from "@/lib/roles";
import { LayoutDashboard, CalendarDays, Users as UsersIcon, Building2, Bell, Settings, LogOut, Menu, X, Receipt, Megaphone, MessageSquareText, Shield, UserCog, KeyRound, Boxes, Inbox as InboxIcon, Sun, Moon, BarChart3, Send } from "lucide-react";
import ChangePasswordModal from "@/components/ChangePasswordModal";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTheme } from "@/contexts/ThemeContext";
import OutboxStatusWidget from "@/components/OutboxStatusWidget";
import { useState, useEffect } from "react";
import api from "@/lib/api";
import { cn } from "@/lib/utils";

const TENANT_NAV = [
  { to: "/dashboard", key: "dashboard", icon: LayoutDashboard, end: false },
  { to: "/eventos", key: "events", icon: CalendarDays },
  { to: "/promotores", key: "promoters", icon: UsersIcon },
  { to: "/inbox", key: "inbox", icon: InboxIcon },
  { to: "/clientes", key: "clients", icon: Building2 },
  { to: "/faturacao", key: "invoices", icon: Receipt },
  { to: "/stocks", key: "stock", icon: Boxes },
  { to: "/templates", key: "templates", icon: MessageSquareText },
  { to: "/notificacoes", key: "notifications", icon: Bell },
  { to: "/alertas", key: "alerts", icon: Megaphone },
];

const ADMIN_ONLY_NAV = [
  { to: "/utilizadores", key: "users", icon: UserCog },
  { to: "/configuracoes", key: "settings", icon: Settings },
];

const PLATFORM_NAV = [
  { to: "/admin/organizations", key: "organizations", icon: Building2, end: true },
  { to: "/admin/metrics", key: "metrics", icon: BarChart3, end: true },
  { to: "/admin/messages", key: "messageLog", icon: Send, end: true },
];

function NavItems({ nav, onNavigate, unread, inboxUnread, showSuperLink, onSuperClick }) {
  const { t } = useTranslation("nav");
  return (
    <nav className="flex-1 px-3 space-y-1" data-testid="sidebar-nav">
      {nav.map(({ to, key, icon: Icon, end }) => {
        const label = t(key);
        const badge = key === "notifications" ? unread : key === "inbox" ? inboxUnread : 0;
        return (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`nav-${key}`}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors",
                isActive
                  ? "bg-zinc-100 text-zinc-950 font-semibold"
                  : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900",
              )
            }
          >
            <Icon className="w-4 h-4" strokeWidth={2} />
            <span>{label}</span>
            {badge > 0 && (
              <span data-testid={key === "notifications" ? "unread-badge" : `${key}-unread-badge`} className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-zinc-950 text-white text-[10px] font-semibold">
                {badge}
              </span>
            )}
          </NavLink>
        );
      })}
      {showSuperLink && (
        <Link
          to="/admin/organizations"
          onClick={onSuperClick}
          data-testid="nav-superadmin"
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 mt-4 border-t border-zinc-100 pt-4"
        >
          <Shield className="w-4 h-4" strokeWidth={2} />
          <span>{t("superadminPanel")}</span>
        </Link>
      )}
    </nav>
  );
}

export default function Layout({ platformView = false }) {
  const { t } = useTranslation("nav");
  const { user, logout } = useAuth();
  const { org } = useOrg();
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const tPath = useTenantPath();
  const [unread, setUnread] = useState(0);
  const [inboxUnread, setInboxUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    if (platformView) return;
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/notifications");
        if (mounted) setUnread(data.filter((n) => !n.read).length);
      } catch {/* ignore */}
      try {
        const { data } = await api.get("/inbox/unread-count");
        if (mounted) setInboxUnread(data?.conversations_unread || 0);
      } catch {/* ignore */}
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { mounted = false; clearInterval(id); };
  }, [platformView]);

  const { theme, toggle } = useTheme();
  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const nav = platformView
    ? PLATFORM_NAV
    : [
        ...TENANT_NAV,
        ...(isAdmin ? ADMIN_ONLY_NAV : []),
      ].map((n) => ({ ...n, to: tPath(n.to) }));

  const showSuperLink = user?.role === "superadmin" && !platformView;

  const brandName = platformView ? "PromoCrew" : (org?.name || "PromoCrew");
  const brandSub = platformView ? t("platformPanel") : (org ? "by Do IT Happen" : "PromoCrew");

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 border-r border-zinc-200 bg-white flex-col fixed h-screen z-30">
        <div className="px-6 py-6 border-b border-zinc-100 flex items-center gap-3">
          {platformView ? (
            <img src="/brand/dih-icon-cor.png" alt="Do IT Happen" data-testid="brand-platform-logo" className="w-10 h-10 object-contain" />
          ) : org?.logo_url ? (
            <img src={org.logo_url} alt="" className="w-10 h-10 rounded-md object-cover bg-zinc-100" />
          ) : (
            <div
              className="w-10 h-10 rounded-md flex items-center justify-center text-white text-sm font-semibold"
              style={{ background: org?.primary_color || "#0a0a0a" }}
              data-testid="brand-mark"
            >
              {(brandName || "P").slice(0, 1)}
            </div>
          )}
          <div className="min-w-0">
            <div data-testid="brand-name" className="font-display font-bold text-base tracking-tight text-zinc-950 truncate">{brandName}</div>
            <div className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider font-medium">{brandSub}</div>
          </div>
        </div>
        <div className="flex-1 py-4 overflow-y-auto">
          <NavItems nav={nav} unread={unread} inboxUnread={inboxUnread} showSuperLink={showSuperLink} />
        </div>
        <div className="border-t border-zinc-100 p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 rounded-full bg-zinc-900 text-white text-xs font-semibold flex items-center justify-center">
              {(user?.name || "A").slice(0, 1)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-zinc-900 truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
              {user?.role === "superadmin" ? (
                <div data-testid="role-badge" className="text-[10px] text-amber-700 uppercase tracking-wider font-semibold mt-0.5">{t("roleLabels.superadmin")}</div>
              ) : user?.role ? (
                <div data-testid="role-badge" className="text-[10px] text-zinc-600 uppercase tracking-wider font-semibold mt-0.5">{t(`roleLabels.${user.role}`, user.role)}</div>
              ) : null}
            </div>
            <LanguageSwitcher />
          </div>
          <button
            data-testid="theme-toggle-button"
            onClick={toggle}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg transition-colors"
          >
            {theme === "dark" ? <Sun className="w-4 h-4" strokeWidth={2} /> : <Moon className="w-4 h-4" strokeWidth={2} />}
            {theme === "dark" ? t("lightMode", "Modo claro") : t("darkMode", "Modo escuro")}
          </button>
          <button
            data-testid="change-password-button"
            onClick={() => setShowChangePassword(true)}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg transition-colors"
          >
            <KeyRound className="w-4 h-4" strokeWidth={2} />
            {t("changePassword")}
          </button>
          <button
            data-testid="logout-button"
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" strokeWidth={2} />
            {t("logout")}
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-zinc-200 px-4 h-14 flex items-center justify-between">
        <div className="font-display font-bold text-lg text-zinc-950">{brandName}</div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <button
            data-testid="theme-toggle-mobile"
            onClick={toggle}
            className="p-2 rounded-lg hover:bg-zinc-100 text-zinc-600"
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="p-2 rounded-lg hover:bg-zinc-100"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 pt-14">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpen(false)} />
          <aside className="absolute top-14 left-0 bottom-0 w-72 bg-white flex flex-col">
            <div className="flex-1 py-4 overflow-y-auto">
              <NavItems
                nav={nav}
                onNavigate={() => setOpen(false)}
                unread={unread}
                inboxUnread={inboxUnread}
                showSuperLink={showSuperLink}
                onSuperClick={() => setOpen(false)}
              />
            </div>
            <div className="border-t border-zinc-100 p-4">
              <button
                data-testid="logout-button-mobile"
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-zinc-600 hover:bg-zinc-50 rounded-lg"
              >
                <LogOut className="w-4 h-4" /> {t("logout")}
              </button>
            </div>
          </aside>
        </div>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 lg:ml-64 pt-14 lg:pt-0 min-h-screen">
        <div className="fixed z-40 top-2.5 right-14 lg:top-4 lg:right-6">
          <OutboxStatusWidget />
        </div>
        <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10 max-w-[1400px] mx-auto">
          <Outlet />
        </div>
      </main>

      {showChangePassword && <ChangePasswordModal onClose={() => setShowChangePassword(false)} />}
    </div>
  );
}
